import React, { useMemo } from 'react'
import { Card, Collapse, Divider } from 'antd'
import { MinusOutlined, PlusOutlined, WalletOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { currency, formatDate } from 'utils/formatting'
import { ItemRow, ItemsRow } from 'components/apps/Reports/PosShift/Detail/ItemsRow'
import SectionHeader from 'components/apps/Reports/PosShift/Detail/SectionHeader'
import HelpHint from 'components/apps/Reports/PosShift/Detail/HelpHint'
import styles from './detail.module.scss'

const { Panel } = Collapse

const expandIcon = ({ isActive }) => (
  <span className={`${styles.expandIcon} ${isActive ? styles.expandIconActive : ''}`}>
    {isActive ? <MinusOutlined /> : <PlusOutlined />}
  </span>
)

const ExpandableCashRow = ({ title, amount, entries, parens }) => {
  if (!entries || entries.length === 0) {
    return <ItemRow title={title} amount={amount} />
  }

  const renderEntryTitle = (entry) => {
    const meta = [
      entry.ref_number,
      entry.trans_date ? formatDate(entry.trans_date, 'DD MMM YYYY') : '',
    ]
      .filter(Boolean)
      .join(' · ')

    return (
      <span>
        <span className={styles.paymentDetailInvoiceRef}>{entry.memo || entry.desc || '-'}</span>
        {meta ? <div className={styles.paymentDetailPaymentRef}>{meta}</div> : null}
      </span>
    )
  }

  return (
    <Collapse ghost expandIcon={expandIcon} className={styles.productsCollapse}>
      <Panel
        key="detail"
        header={
          <div className={styles.categoryHeader}>
            <span className={styles.categoryName}>{title}</span>
            <span className={styles.categoryTotal}>{amount}</span>
          </div>
        }
      >
        <ItemsRow>
          {entries.map((entry) => {
            const value = currency(entry.amount)
            return (
              <ItemRow
                key={entry.id}
                title={renderEntryTitle(entry)}
                amount={parens ? `(${value})` : value}
              />
            )
          })}
        </ItemsRow>
      </Panel>
    </Collapse>
  )
}

const CashMovement = ({ data, loading }) => {
  const { formatMessage } = useIntl()

  const cashPayment = Number(data?.cash_payment) || 0
  const cashRefund = Number(data?.cash_refund) || 0
  const cashOut = Math.abs(Number(data?.cash_out) || 0)

  const { cashInList, cashOutList } = useMemo(() => {
    const list = Array.isArray(data?.cash_in_outs) ? data.cash_in_outs : []
    return {
      cashInList: list.filter((item) => item?.type === 'in'),
      cashOutList: list.filter((item) => item?.type === 'out'),
    }
  }, [data])

  return (
    <Card
      className={styles.card}
      loading={loading}
      title={
        <SectionHeader
          icon={<WalletOutlined />}
          iconClassName={styles.iconGreen}
          title={formatMessage({ id: 'report.cash_movement_physical' })}
        />
      }
    >
      <ItemsRow>
        <ItemRow
          title={formatMessage({ id: 'report.initial_capital' })}
          amount={currency(data?.opening_balance)}
        />
        <ExpandableCashRow
          title={formatMessage({ id: 'report.cash_in' })}
          amount={currency(data?.cash_in)}
          entries={cashInList}
        />
        <ItemRow
          title={formatMessage({ id: 'report.received_cash' })}
          amount={currency(cashPayment)}
        />
        <ExpandableCashRow
          title={formatMessage({ id: 'report.cash_out' })}
          amount={`(${currency(cashOut)})`}
          entries={cashOutList}
          parens
        />
        <ItemRow
          title={formatMessage({ id: 'report.cash_refund' })}
          amount={`(${currency(cashRefund)})`}
        />
        <Divider style={{ margin: '4px 0' }} />
        <ItemRow
          title={
            <span>
              {formatMessage({ id: 'report.cash_in_drawer' })}
              <HelpHint title={formatMessage({ id: 'report.cash_movement_formula' })} />
            </span>
          }
          amount={currency(data?.cash_sales)}
          bold
        />
      </ItemsRow>
    </Card>
  )
}

export default CashMovement
